import { Box, Button, Link, Text, colors } from '@factorialco/deprecated-design-system'
import { isEmpty } from 'lodash'

import i18n from 'modules/core/lib/i18n'

const statusPage = 'https://status.factorialhr.com'

const CrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="100" height="70" fill="none" viewBox="0 0 100 70">
    <path
      fill={colors.grey400}
      d="M5.6 33.99a4.695 4.695 0 01-.35-1.776c0-2.72 2.352-4.928 5.25-4.928 1.077 0 2.084.308 2.915.831C14.93 25.653 17.757 24 21 24c4.834 0 8.75 3.676 8.75 8.214 0 .139-.006.278-.01.416 3.062 1.011 5.26 3.753 5.26 6.977C35 43.69 31.473 47 27.125 47H7c-3.866 0-7-2.942-7-6.571 0-3.178 2.406-5.833 5.6-6.438zM84.88 10.647c-.115-.31-.18-.65-.18-1.004 0-1.538 1.21-2.786 2.7-2.786.554 0 1.072.174 1.5.47C89.677 5.934 91.131 5 92.8 5c2.486 0 4.5 2.078 4.5 4.643 0 .078-.003.157-.006.235C98.87 10.45 100 11.999 100 13.82 100 16.128 98.186 18 95.95 18H85.6c-1.988 0-3.6-1.663-3.6-3.714 0-1.796 1.237-3.297 2.88-3.64z"
    />
    <path
      fill="#fff"
      stroke={colors.grey800}
      strokeWidth="2"
      d="M71 59H30a1 1 0 01-1-1V2a1 1 0 011-1h28.371L72 14.81V58a1 1 0 01-1 1z"
    />
    <path
      stroke={colors.grey800}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeMiterlimit="10"
      strokeWidth="2"
      d="M55.314 24L44 35.314M55.314 35.314L44 24"
    />
    <path fill={colors.grey000White} d="M28 18h2v2h-2zM28 14h2v2h-2zM28 10h2v2h-2z" />
    <path
      fill={colors.grey200}
      d="M88.68 50.203c.333-.836.52-1.75.52-2.703 0-4.14-3.494-7.5-7.8-7.5-1.6 0-3.096.469-4.33 1.266C74.818 37.516 70.617 35 65.8 35c-7.182 0-13 5.594-13 12.5 0 .21.008.422.016.633C48.266 49.672 45 53.843 45 58.75 45 64.96 50.24 70 56.7 70h29.9C92.344 70 97 65.523 97 60c0-4.836-3.575-8.875-8.32-9.797z"
    />
  </svg>
)

interface CopyProps {
  crashButtonLabel?: string
  crashDescription?: string
}

const getCopies = ({ crashButtonLabel, crashDescription }: CopyProps): Record<string, string> => {
  if (!i18n || isEmpty(i18n.translations)) {
    // i18n still not initialized, getting copies manually...
    const language: string = (navigator.language || (navigator as any).browserLanguage)
      .toLowerCase()
      .split('-')[0]

    const translations: Record<string, any> = {
      en: {
        title: 'Oops...',
        retry:
          'We’re sorry, but we couldn’t fulfill your request. Please try again in a few minutes, or check our ',
        link: 'status page',
        button: 'Try again',
      },
      es: {
        title: 'Ups...',
        retry:
          'Lo sentimos, pero no pudimos cumplir con tu solicitud. Intenta entrar de nuevo en unos minutos, o consulta nuestra ',
        link: 'página de estado',
        button: 'Intentar de nuevo',
      },
      ca: {
        title: 'Ups...',
        retry:
          'Ho sentim, però no hem pogut complir la teva sol·licitud. Torna a entrar passats uns minuts, o consulta la nostra ',
        link: "pàgina d'estat",
        button: 'Intentar-ho de nou',
      },
    }

    return translations[language] || translations.en
  }

  return {
    title: i18n.t('common.crash.title'),
    retry: crashDescription || i18n.t('common.crash.retry'),
    button: crashButtonLabel || i18n.t('common.crash.button'),
  }
}

interface Props {
  compact?: boolean
  isBootstrap?: boolean
  crashButtonRoute?: string
  crashButtonLabel?: string
  crashDescription?: string
}

const CrashLogo = ({
  compact = false,
  isBootstrap = false,
  crashButtonLabel,
  crashButtonRoute,
  crashDescription,
}: Props) => {
  const copies = getCopies({
    crashButtonLabel,
    crashDescription,
  })

  const onClick = () => {
    if (crashButtonRoute) {
      window.location.href = crashButtonRoute
    } else {
      window.location.reload()
    }
  }

  return (
    <Box
      width="full"
      height="full"
      justifyContent="center"
      alignItems="center"
      padding={!compact ? 's24' : null}
    >
      <Box
        width="full"
        height="full"
        textAlign="center"
        style={isBootstrap ? { maxWidth: '600px', height: 'auto' } : {}}
      >
        <Box
          width="full"
          height="full"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          padding="s64"
          background="grey000White"
          border={{
            all: {
              width: 's1',
              style: 'solid',
              color: 'radicalRed200',
            },
          }}
          borderRadius={{
            all: 'abs008',
          }}
        >
          {isBootstrap && (
            <Box paddingBottom="s16">
              <CrashIcon />
            </Box>
          )}
          <Text.Header2 htmlTag="div">{copies.title}</Text.Header2>
          <Box alignItems="center" paddingX="s8" paddingY="s32">
            <Text color="grey800" size="300">
              {copies.retry}
            </Text>
            {isBootstrap && (
              <Link.Href
                type="default"
                external
                href={statusPage}
                rel="nofollow noopener noreferrer"
                target="_blank"
              >
                {copies.link}
              </Link.Href>
            )}
          </Box>
          <Button type="negative" onClick={onClick}>
            {copies.button}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default CrashLogo
